import { useState } from "react";
import { PiBell } from "react-icons/pi";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

type EnableNotificationsPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called with the result of `Notification.requestPermission()`.
   * The popup auto-closes on 'granted' or 'denied'; the parent decides
   * whether to show it again (e.g., persist a "dismissed" flag).
   */
  onPermissionResult?: (permission: NotificationPermission) => void;
};

/**
 * Lightweight popup that triggers the OS-native notification permission prompt.
 *
 * Parent responsibilities:
 *  - Decide when to show (PWA check, service worker check, permission !== 'default')
 *  - Persist a "dismissed" flag in localStorage if needed
 *  - Handle subscription / backend sync after 'granted' (see usePushNotification)
 */
export default function EnableNotificationsPopup({
  isOpen,
  onClose,
  onPermissionResult,
}: EnableNotificationsPopupProps) {
  const [isRequesting, setIsRequesting] = useState(false);

  if (!isOpen) return null;

  const handleEnable = async () => {
    if (isRequesting) return;
    if (typeof window === "undefined" || typeof Notification === "undefined")
      return;

    setIsRequesting(true);
    try {
      const permission = await Notification.requestPermission();
      onPermissionResult?.(permission);
      if (permission === "granted" || permission === "denied") {
        onClose();
      }
    } catch (error) {
      console.error(
        "[EnableNotificationsPopup] requestPermission failed",
        error,
      );
      onPermissionResult?.("denied");
      onClose();
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <div className="fixed top-4 left-4 right-4 z-[10001] animate-in slide-in-from-top-5 fade-in duration-300 w-fit">
      <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-2xl shadow-black/10">
        <button
          onClick={onClose}
          disabled={isRequesting}
          className="absolute right-2 top-2 rounded-full bg-gray-50/50 p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="flex gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#3674cf] text-white">
            <PiBell size={24} />
          </div>
          <div className="pr-4">
            <h3 className="mb-1 font-bold leading-tight text-gray-900">
              Enable Notifications
            </h3>
            <p className="mb-3 pr-6 text-sm leading-snug text-gray-500">
              Get instant alerts for new leads.
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleEnable}
                disabled={isRequesting}
                className={cn(
                  "rounded-lg bg-[#3674cf] px-4 py-2 text-sm font-medium text-white shadow-md",
                  "transition-all hover:bg-[#3674cf] active:scale-[0.98]",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                )}
              >
                {isRequesting ? "Enabling..." : "Enable Now"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
